/**
 * MyMeasurementsTab
 * Shows user's assigned measurements with actions (draughter_site)
 * Extracted from SiteDraughterDashboard.js
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { measurementAPI } from '../../../services/api';
import { toast } from 'react-toastify';

export default function MyMeasurementsTab() {
  const navigate = useNavigate();
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMeasurements();
  }, []);

  async function fetchMeasurements() {
    setLoading(true);
    try {
      const response = await measurementAPI.getMy();
      setMeasurements(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching measurements:', err);
      setError(err.message || 'Failed to load measurements');
      toast.error('Failed to load measurements');
    } finally {
      setLoading(false);
    }
  }

  function getStatusBadge(status) {
    const badges = {
      scheduled: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      scheduled: 'Scheduled',
      assigned: 'Assigned',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  }

  function formatDateTime(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (measurements.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No measurements assigned</h3>
        <p className="mt-1 text-sm text-gray-500">
          You have no assigned measurements. Measurements will appear here once assigned by SID/ASID.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Project
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Agent
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Leader
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '180px' }}>
              Location
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Property
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date & Time
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              SID
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {measurements.map((measurement) => {
            // Extract all agents and leaders from salesAgents
            const salesAgents = measurement.designProject?.salesAgents || [];
            const agents = salesAgents.map(sa => sa.agent).filter(Boolean);
            const leaders = salesAgents
              .map(sa => sa.salesLeader)
              .filter(Boolean)
              .filter((leader, index, self) =>
                self.findIndex(l => l.id === leader.id) === index // unique leaders
              );

            // Fallback to salesForm agent if no salesAgents
            if (agents.length === 0 && measurement.designProject?.salesForm?.agent) {
              agents.push(measurement.designProject.salesForm.agent);
              if (measurement.designProject.salesForm.agent.leader) {
                leaders.push(measurement.designProject.salesForm.agent.leader);
              }
            }

            // Get SID from teamMembers array
            const sidMember = measurement.designProject?.teamMembers?.find(
              tm => (tm.roleType === 'sid' || tm.roleType === 'asid') && tm.isActive !== false
            );
            const projectSid = sidMember?.user;

            // Build compact address (3 lines max)
            const salesForm = measurement.designProject?.salesForm;
            const addressLine1 = salesForm?.addressLine1 || '';
            const addressLine2 = salesForm?.addressLine2 || '';
            const postcode = salesForm?.postcode || '';
            const city = salesForm?.city || '';
            const state = salesForm?.state || '';

            const locationLine1 = addressLine1;
            const locationLine2 = addressLine2 || `${postcode} ${city}`.trim();
            const locationLine3 = addressLine2 ? `${postcode} ${city}, ${state}`.trim() : state;

            return (
            <tr key={measurement.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 whitespace-nowrap">
                <div
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                  onClick={() => navigate(`/projects/${measurement.designProject?.id}`)}
                >
                  {measurement.designProject?.projectNumber}
                </div>
                <div className="text-xs text-gray-500">
                  {salesForm?.clientName}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="text-sm text-gray-900">
                  {agents.length > 0
                    ? agents.map(a => a.nickname || a.fullName).join(', ')
                    : 'N/A'}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="text-sm text-gray-900">
                  {leaders.length > 0
                    ? leaders.map(l => l.nickname || l.fullName).join(', ')
                    : 'N/A'}
                </div>
              </td>
              <td className="px-4 py-3" style={{ maxWidth: '200px' }}>
                <div className="text-xs text-gray-700 leading-tight">
                  {locationLine1 && <div className="truncate" title={locationLine1}>{locationLine1}</div>}
                  {locationLine2 && <div className="truncate text-gray-500" title={locationLine2}>{locationLine2}</div>}
                  {locationLine3 && locationLine3 !== state && <div className="truncate text-gray-500" title={locationLine3}>{locationLine3}</div>}
                  {!locationLine1 && !locationLine2 && (
                    <div className="truncate" title={measurement.location}>{measurement.location || 'N/A'}</div>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900">{measurement.propertyType || 'N/A'}</div>
                {measurement.propertySize && (
                  <div className="text-xs text-gray-500">{measurement.propertySize} sqft</div>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {formatDateTime(measurement.scheduledDate)}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {projectSid?.nickname || projectSid?.fullName || 'N/A'}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                {getStatusBadge(measurement.status)}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
